import { Inject, Injectable, NgZone } from '@angular/core';
import { collectionSnapshots$, docData$ } from '../utils/FirestoreRx';
import { IClassUser } from '../models/classUser.model';
import { IClass } from '../models/class.model';
import { Firestore, collection, doc, runTransaction } from 'firebase/firestore';
import {
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  of,
  switchMap,
  take,
} from 'rxjs';
import { IUser } from '../models/user.model';
import { IUserWithReservations } from '../models/userWithReservations.model';
import { map } from 'rxjs/operators';
import { EmailNotVerifiedError } from '../errors/EmailNotVerifiedError';
import { ClassFullError } from '../errors/ClassFullError';
import { ReservationCapReachedError } from '../errors/ReservationCapReachedError';
import { NoReservationToRemoveError } from '../errors/NoReservationToRemoveError';
import { TrialAlreadyUsedError } from '../errors/TrialAlreadyUsedError';
import { FIRESTORE } from '../firebase.providers';

@Injectable({
  providedIn: 'root',
})
export class ReservationService {
  private selectedDateSubject: Subject<Date> = new ReplaySubject<Date>();
  selectedDate$: Observable<Date> = this.selectedDateSubject.asObservable();

  constructor(
    @Inject(FIRESTORE) private db: Firestore,
    private zone: NgZone,
  ) {}

  setSelectedDate(date: Date): void {
    this.selectedDateSubject.next(date);
  }

  getSelectedDate(): Observable<Date> {
    return this.selectedDate$;
  }

  getUsersForClassWithAmountOfReservations(
    classId: string,
  ): Observable<IUserWithReservations[]> {
    return collectionSnapshots$<IClassUser>(
      collection(this.db, `classes/${classId}/users`),
      this.zone,
    ).pipe(
      switchMap((classUsersSnap) => {
        // Filter BEFORE the join, not after. Every mirror opened a live listener on that
        // member's user document and the result was discarded a moment later, so a
        // member who booked and cancelled kept costing a listener on every admin visit
        // to that class, for as long as the mirror survived.
        const booked = classUsersSnap.filter(
          (snap) => (snap.data.reservations ?? 0) > 0,
        );
        // combineLatest([]) never emits, so an empty list left the dialog on its spinner
        // for ever instead of saying nobody has booked.
        if (booked.length === 0) {
          return of([] as IUserWithReservations[]);
        }
        // One-shot reads, not listeners. A member's name and e-mail do not change while
        // an admin has the dialog open, and a live listener per attendee meant a full
        // class held 40 of them for as long as the dialog lived. The subcollection
        // listener above still drives re-renders when a booking changes, and re-runs
        // this map, so the data stays current.
        const usersObservables = booked.map((classUserSnap) => {
          const classUser = classUserSnap.data;
          const userId = classUserSnap.id;
          return docData$<IUser>(
            doc(this.db, `users/${userId}`),
            this.zone,
          ).pipe(
            take(1),
            map((user) => ({
              id: userId,
              firstName: user?.firstName || '',
              lastName: user?.lastName || '',
              email: user?.email || '',
              isAdmin: user?.isAdmin || false,
              creationDate: user?.creationDate || 0,
              reservationsForCurrentClass: classUser.reservations || 0,
              chipnumber: user?.chipnumber || '',
              trial: user?.trial || false,
            })),
          );
        });
        return combineLatest(usersObservables);
      }),
    );
  }

  /** Books one spot. Rejects with a typed, code-carrying error on every failure path
   *  so the caller can show the real reason instead of a false success. */
  /**
   * Firestore settles a transaction on its own WebChannel callback, outside Angular's
   * zone. A caller's .then/.finally then runs outside it too: the "Reserveer" button
   * stayed disabled and the counter kept its old value until the next click or a
   * snapshot happened to arrive. Resolving through zone.run makes every continuation
   * schedule change detection, the same seam fix FirestoreRx applies to the listeners.
   */
  private inZone<T>(work: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      work().then(
        (value) => this.zone.run(() => resolve(value)),
        (error) => this.zone.run(() => reject(error)),
      );
    });
  }

  async addReservation(userId: string, classId: string): Promise<IClass> {
    const classRef = doc(this.db, `classes/${classId}`);
    const userRef = doc(this.db, `users/${userId}`);
    const userClassRef = doc(this.db, `users/${userId}/classes/${classId}`);
    const classUserRef = doc(this.db, `classes/${classId}/users/${userId}`);

    return this.inZone(() =>
      runTransaction(this.db, async (transaction) => {
        // The three reads do not depend on each other: issued together they cost one
        // round-trip instead of three, which on a phone in a bad spot is the difference
        // between a booking that lands in two seconds and one that takes six.
        const [classDoc, classUserDoc, userDoc] = await Promise.all([
          transaction.get(classRef),
          transaction.get(classUserRef),
          transaction.get(userRef),
        ]);

        const iClass = classDoc?.data() as IClass | undefined;
        const classUser = classUserDoc?.data() as IClassUser | undefined;
        const user = userDoc?.data() as IUser | undefined;

        if (!iClass) {
          throw new Error('Class not found');
        }
        if (!user?.emailVerified) {
          throw new EmailNotVerifiedError();
        }
        // A proefles account may book exactly one class ever; trialUsed is the gate.
        if (user.trial && user.trialUsed) {
          throw new TrialAlreadyUsedError();
        }

        // ?? 0 keeps first-time bookers and legacy docs out of NaN arithmetic.
        const currentReservations = iClass.currentReservations ?? 0;
        const memberReservations = classUser?.reservations ?? 0;

        if (currentReservations + 1 > iClass.maxReservations) {
          throw new ClassFullError();
        }
        if (memberReservations >= 5 && !user.isAdmin) {
          throw new ReservationCapReachedError();
        }

        const classReservations = currentReservations + 1;
        const userReservations = memberReservations + 1;
        iClass.currentReservations = classReservations;

        // dateEpoch on the mirrors lets reads filter to upcoming classes server-side,
        // so a member's booking-page reads stay flat regardless of attendance history.
        transaction.set(
          classRef,
          { currentReservations: classReservations },
          { merge: true },
        );
        transaction.set(
          classUserRef,
          {
            userId,
            reservations: userReservations,
            dateEpoch: iClass.dateEpoch,
          },
          { merge: true },
        );
        transaction.set(
          userClassRef,
          {
            classId,
            reservations: userReservations,
            dateEpoch: iClass.dateEpoch,
          },
          { merge: true },
        );
        if (user.trial) {
          // Flipped inside the booking transaction, so two concurrent bookings can never
          // both pass the trialUsed check above. The proefles stays a single class.
          transaction.set(userRef, { trialUsed: true }, { merge: true });
        }

        return iClass;
      }),
    );
  }

  /** Cancels one spot. Requires an existing reservation, else the class counter would
   *  drift below the real occupancy. Both counters clamp at 0 so any existing drift
   *  corrects instead of going negative. */
  async removeReservation(userId: string, classId: string): Promise<IClass> {
    const classRef = doc(this.db, `classes/${classId}`);
    const userRef = doc(this.db, `users/${userId}`);
    const userClassRef = doc(this.db, `users/${userId}/classes/${classId}`);
    const classUserRef = doc(this.db, `classes/${classId}/users/${userId}`);

    return this.inZone(() =>
      runTransaction(this.db, async (transaction) => {
        // The three reads do not depend on each other: issued together they cost one
        // round-trip instead of three, which on a phone in a bad spot is the difference
        // between a booking that lands in two seconds and one that takes six.
        const [classDoc, classUserDoc, userDoc] = await Promise.all([
          transaction.get(classRef),
          transaction.get(classUserRef),
          transaction.get(userRef),
        ]);

        const iClass = classDoc?.data() as IClass | undefined;
        const classUser = classUserDoc?.data() as IClassUser | undefined;
        const user = userDoc?.data() as IUser | undefined;

        if (!iClass) {
          throw new Error('Class not found');
        }
        if (!user?.emailVerified) {
          throw new EmailNotVerifiedError();
        }

        const memberReservations = classUser?.reservations ?? 0;
        if (memberReservations <= 0) {
          throw new NoReservationToRemoveError();
        }

        const classReservations = Math.max(
          0,
          (iClass.currentReservations ?? 0) - 1,
        );
        const userReservations = memberReservations - 1;
        iClass.currentReservations = classReservations;

        transaction.set(
          classRef,
          { currentReservations: classReservations },
          { merge: true },
        );
        if (userReservations === 0) {
          // Delete rather than leave a mirror at reservations: 0. Those documents are
          // invisible in the UI but permanent: they record that this member was once on
          // this class after they cancelled, and every query over the subcollection reads
          // them. AccountService.releaseClass already deletes them on erasure, so leaving
          // them here was the odd one out.
          transaction.delete(classUserRef);
          transaction.delete(userClassRef);
        } else {
          transaction.set(
            classUserRef,
            {
              userId,
              reservations: userReservations,
              dateEpoch: iClass.dateEpoch,
            },
            { merge: true },
          );
          transaction.set(
            userClassRef,
            {
              classId,
              reservations: userReservations,
              dateEpoch: iClass.dateEpoch,
            },
            { merge: true },
          );
        }
        if (user.trial && iClass.dateEpoch > Date.now()) {
          // Cancelling a class that hasn't started frees the trial to be used elsewhere;
          // a class already in the past stays used, so attend-then-rebook is blocked.
          transaction.set(userRef, { trialUsed: false }, { merge: true });
        }

        return iClass;
      }),
    );
  }
}
